<template>
  <div class="container-fluid p-3">
    <div class="flex items-center justify-between">
      <div class="min-w-0 flex-1 mr-4">
        <h1 class="text-xl font-bold text-foreground">Employee Management - {{ user.business.business_name }}</h1>
        <p class="text-muted-foreground text-sm mt-1">Manage employee feedback and review requests</p>
      </div>
      <div class="flex items-center space-x-4 flex-shrink-0">
        <button
          class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2 border-border"
          @click="openLeaderboard"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-layout-grid mr-2 h-4 w-4">
            <rect width="7" height="7" x="3" y="3" rx="1"></rect>
            <rect width="7" height="7" x="14" y="3" rx="1"></rect>
            <rect width="7" height="7" x="14" y="14" rx="1"></rect>
            <rect width="7" height="7" x="3" y="14" rx="1"></rect>
          </svg>
          View Leaderboard
        </button>
        <button
          class="btn bg-primary space-x-3 round-xl px-4 request-review-btn"
          @click="showAddEmployee"
        >
          <i class="uil uil-plus"></i>
          <span class="text-sm font-medium"> Add Employee</span>
        </button>
        <router-link
          :to="{
            name: 'request-reviews',
            params: {
              /* eslint-disable-next-line vue/camelcase */
              unique_id: user.business.unique_id + '-' + user.business.id,
            },
          }"
          class="btn bg-primary space-x-3 round-xl px-4 request-review-btn"
          target="_blank"
        >
          <i class="uil uil-star"></i>
          <span class="text-sm font-medium"> Request Review</span>
        </router-link>
      </div>
    </div>
    <hr class="pb-4 pt-0" style="margin: 0 -40px 0 -20px !important" />
    <div class="px-2">
      <div class="bg-card/50 rounded-2xl p-6 mb-6 border border-border/50">
        <div class="flex items-center justify-between mb-4">
        <div class="flex items-center space-x-4">
          <div class="flex items-center space-x-2">
            <span class="text-sm text-muted-foreground">Show</span>
            <select
              v-model="datatable.queries.per_page"
              class="border border-border rounded px-2 py-1 text-sm bg-background"
              @change="getEmployees"
            >
              <option v-for="option in [10, 15, 25, 50, 100, 'All']" :key="option" :value="option === 'All' ? 999999 : option">
                {{ option }}
              </option>
            </select>
            <span class="text-sm text-muted-foreground">entries</span>
          </div>
          <div class="flex items-center space-x-1 rounded-lg p-1">
            <button
              class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 rounded-md h-7 w-7 p-0 border-0"
              :class="viewMode === 'list' ? 'request-review-btn' : 'bg-transparent'"
              :style="viewMode === 'list' ? { backgroundColor: 'rgb(36, 99, 235)', color: '#ffffff' } : {}"
              @click="viewMode = 'list'"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-list h-4 w-4">
                <path d="M3 12h.01"></path>
                <path d="M3 18h.01"></path>
                <path d="M3 6h.01"></path>
                <path d="M8 12h13"></path>
                <path d="M8 18h13"></path>
                <path d="M8 6h13"></path>
              </svg>
            </button>
            <button
              class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 rounded-md h-7 w-7 p-0 border-0"
              :class="viewMode === 'grid' ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'hover:bg-accent hover:text-accent-foreground bg-transparent'"
              @click="viewMode = 'grid'"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-layout-grid h-4 w-4">
                <rect width="7" height="7" x="3" y="3" rx="1"></rect>
                <rect width="7" height="7" x="14" y="3" rx="1"></rect>
                <rect width="7" height="7" x="14" y="14" rx="1"></rect>
                <rect width="7" height="7" x="3" y="14" rx="1"></rect>
              </svg>
            </button>
          </div>
        </div>
        <div class="flex items-center space-x-2">
          <span class="text-sm text-muted-foreground">Search:</span>
          <div class="relative">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-search absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.3-4.3"></path>
            </svg>
            <input
              v-model="datatable.queries.search"
              type="search"
              placeholder="Search employees..."
              class="flex h-10 rounded-md border border-input bg-background py-2 pr-3 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 w-64"
              style="padding-left: 2.75rem;"
              @keyup.enter="getEmployees"
            >
          </div>
        </div>
      </div>
      <!-- List View -->
      <div
        v-if="viewMode === 'list'"
        class="rounded-md border border-border overflow-hidden"
      >
        <div class="relative w-full overflow-auto">
          <template v-if="!loadingEmployees">
            <table class="w-full caption-bottom text-sm">
              <thead class="[&_tr]:border-b">
                <tr class="border-b transition-colors data-[state=selected]:bg-muted hover:bg-muted/50 bg-muted/50">
                  <th
                    v-for="field in dbColumns"
                    :key="field.key"
                    class="h-12 px-4 align-middle [&:has([role=checkbox])]:pr-0 font-semibold text-foreground bg-muted/50"
                    :class="{
                      'text-left': field.key !== 'phone' && field.key !== 'email' && field.key !== 'rating' && field.key !== 'mentions_count' && field.key !== 'positive_feedback_count' && field.key !== 'negative_feedback_count' && field.key !== 'actions',
                      'text-center': field.key === 'phone' || field.key === 'email' || field.key === 'rating' || field.key === 'mentions_count' || field.key === 'positive_feedback_count' || field.key === 'negative_feedback_count' || field.key === 'actions',
                      'cursor-pointer': field.sortable,
                      'hover:bg-muted/70': field.sortable,
                    }"
                    @click="field.sortable && handleSort(field.key)"
                  >
                    {{ field.label }}
                    <span
                      v-if="
                        field.sortable &&
                        datatable.queries.sort_by === field.key
                      "
                      class="ml-1"
                    >
                      <i
                        v-if="datatable.queries.sort_desc"
                        class="uil uil-arrow-down text-xs"
                      ></i>
                      <i v-else class="uil uil-arrow-up text-xs"></i>
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody class="[&_tr:last-child]:border-0">
                <tr
                  v-for="item in employees"
                  :key="item.id"
                  class="border-b transition-colors data-[state=selected]:bg-muted hover:bg-muted/50"
                >
                  <td class="p-4 align-middle [&:has([role=checkbox])]:pr-0 text-muted-foreground">
                    {{ item.id }}
                  </td>
                  <td class="p-4 align-middle [&:has([role=checkbox])]:pr-0 font-medium text-foreground">
                    {{ item.name }}
                  </td>
                  <td class="p-4 align-middle [&:has([role=checkbox])]:pr-0">
                    <div class="flex items-center justify-center">
                      <svg
                        v-if="item.phone"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="h-4 w-4 text-green-600"
                      >
                        <path d="M20 6 9 17l-5-5"></path>
                      </svg>
                      <svg
                        v-else
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="h-4 w-4 text-red-600"
                      >
                        <path d="M18 6 6 18"></path>
                        <path d="m6 6 12 12"></path>
                      </svg>
                    </div>
                  </td>
                  <td class="p-4 align-middle [&:has([role=checkbox])]:pr-0">
                    <div class="flex items-center justify-center">
                      <svg
                        v-if="item.email"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="h-4 w-4 text-green-600"
                      >
                        <path d="M20 6 9 17l-5-5"></path>
                      </svg>
                      <svg
                        v-else
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="h-4 w-4 text-red-600"
                      >
                        <path d="M18 6 6 18"></path>
                        <path d="m6 6 12 12"></path>
                      </svg>
                    </div>
                  </td>
                  <td class="p-4 align-middle [&:has([role=checkbox])]:pr-0">
                    <div class="flex items-center space-x-2">
                      <a
                        :href="item.card_id ? tapUrl + 'c/' + item.card_id : tapUrl + 'r/' + user.business.unique_id + '/' + item.slug"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-primary text-sm font-mono truncate max-w-[200px] hover:underline"
                      >
                        {{
                          item.card_id
                            ? tapUrl + 'c/' + item.card_id
                            : tapUrl +
                              'r/' +
                              user.business.unique_id +
                              '/' +
                              item.slug
                        }}
                      </a>
                      <button
                        type="button"
                        class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:text-accent-foreground rounded-md h-6 w-6 p-0 border-0 bg-transparent"
                        @click="
                          copyLink(
                            item.card_id
                              ? tapUrl + 'c/' + item.card_id
                              : tapUrl +
                                  'r/' +
                                  user.business.unique_id +
                                  '/' +
                                  item.slug,
                          )
                        "
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3">
                          <rect width="14" height="14" x="8" y="8" rx="2" ry="2"></rect>
                          <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"></path>
                        </svg>
                      </button>
                    </div>
                  </td>
                  <td class="p-4 align-middle [&:has([role=checkbox])]:pr-0 text-center">
                    <div class="flex items-center justify-center">
                      <div
                        class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 hover:bg-secondary/80 bg-primary/10 text-primary border-primary/20"
                      >
                        {{ item.rating || '0.0' }}
                      </div>
                    </div>
                  </td>
                  <td class="p-4 align-middle [&:has([role=checkbox])]:pr-0 text-center">
                    <div class="flex items-center justify-center">
                      <div
                        class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 text-foreground"
                      >
                        {{ item.mentions_count || 0 }}
                      </div>
                    </div>
                  </td>
                  <td class="p-4 align-middle [&:has([role=checkbox])]:pr-0 text-center">
                    <div class="flex items-center justify-center space-x-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 text-success">
                        <path d="M7 10v12"></path>
                        <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z"></path>
                      </svg>
                      <div
                        class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 hover:bg-secondary/80 bg-success/10 text-success border-success/20 min-w-[24px] justify-center"
                      >
                        {{ item.positive_feedback_count || 0 }}
                      </div>
                    </div>
                  </td>
                  <td class="p-4 align-middle [&:has([role=checkbox])]:pr-0 text-center">
                    <div class="flex items-center justify-center space-x-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 text-destructive">
                        <path d="M17 14V2"></path>
                        <path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z"></path>
                      </svg>
                      <div
                        class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 hover:bg-secondary/80 bg-destructive/10 text-destructive border-destructive/20 min-w-[24px] justify-center"
                      >
                        {{ item.negative_feedback_count || 0 }}
                      </div>
                    </div>
                  </td>
                  <td class="p-4 align-middle [&:has([role=checkbox])]:pr-0">
                    <div class="flex items-center justify-center space-x-1">
                      <button
                        class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 rounded-md h-8 w-8 p-0 border-0 bg-transparent hover:text-primary"
                        @click="editEmployee(item)"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4">
                          <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                          <path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z"></path>
                        </svg>
                      </button>
                      <button
                        class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 rounded-md h-8 w-8 p-0 border-0 bg-transparent hover:text-destructive"
                        @click="deleteEmployee(item)"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4">
                          <path d="M3 6h18"></path>
                          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                          <line x1="10" x2="10" y1="11" y2="17"></line>
                          <line x1="14" x2="14" y1="11" y2="17"></line>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </template>
          <div v-else class="text-center py-8">
            <b-spinner></b-spinner>
          </div>
        </div>
      </div>
      <!-- Card View -->
      <div
        v-else-if="viewMode === 'grid'"
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
      >
        <template v-if="!loadingEmployees">
          <div
            v-for="item in employees"
            :key="item.id"
            class="bg-card/50 round-xl p-x-1 m-1 border shadow-card mb-4 gap-2"
          >
            <div class="p-3">
              <div class="flex items-center justify-between mb-4">
                <div class="flex items-center space-x-3 flex-1 min-w-0">
                  <div
                    class="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center shadow-md flex-shrink-0"
                  >
                    <i class="uil uil-user text-primary-foreground"></i>
                  </div>
                  <div class="min-w-0 flex-1">
                    <h3
                      class="font-semibold text-foreground text-base truncate"
                      >{{ item.name }}</h3
                    >
                    <p
                      v-if="
                        user &&
                        user.business &&
                        user.business.is_tap_engine &&
                        item.card_id
                      "
                      class="text-xs text-muted-foreground"
                      >Card ID: {{ item.card_id }}</p
                    >
                    <p
                      v-else-if="
                        user && user.business && user.business.is_tap_engine
                      "
                      class="text-xs text-muted-foreground"
                      >ID: {{ item.id }}</p
                    >
                    <p v-else class="text-xs text-muted-foreground"
                      >ID: {{ item.id }}</p
                    >
                  </div>
                </div>
                <div
                  class="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                >
                  <a
                    class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 rounded-md h-7 w-7 p-0 hover:bg-primary/10 hover:text-primary"
                    @click="editEmployee(item)"
                  >
                    <i class="uil uil-edit"></i>
                  </a>
                  <a
                    class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 rounded-md h-7 w-7 p-0 hover:bg-destructive/10 hover:text-destructive"
                    @click="deleteEmployee(item)"
                  >
                    <i class="uil uil-trash-alt"></i>
                  </a>
                </div>
              </div>
              <div class="flex justify-between items-center mb-3">
                <div class="flex items-center space-x-1">
                  <span class="text-xs text-muted-foreground">Phone:</span>
                  <i v-if="item.phone" class="uil uil-check text-success"></i>
                  <i v-else class="uil uil-times text-danger"></i>
                </div>
                <div class="flex items-center space-x-1">
                  <span class="text-xs text-muted-foreground">Email:</span>
                  <i v-if="item.email" class="uil uil-check text-success"></i>
                  <i v-else class="uil uil-times text-danger"></i>
                </div>
              </div>
              <div class="grid grid-cols-3 gap-2 mb-2">
                <div
                  class="flex flex-col justify-between h-full text-center p-1 bg-muted/20 rounded-md"
                >
                  <div
                    class="flex items-center justify-center space-x-1 mb-0.5"
                  >
                    <i class="uil uil-star text-warning"></i>
                    <span class="text-xs font-medium text-foreground">{{
                      item.rating || '0.0'
                    }}</span>
                  </div>
                  <span class="text-xs text-muted-foreground">Rating</span>
                </div>

                <div
                  class="flex flex-col justify-between h-full text-center p-1 bg-muted/20 rounded-md"
                >
                  <div class="text-xs font-medium text-foreground mb-0.5">
                    {{ item.mentions_count || 0 }}
                  </div>
                  <span class="text-xs text-muted-foreground">Mentions</span>
                </div>

                <div
                  class="flex flex-col justify-between h-full text-center p-1 bg-muted/20 rounded-md"
                >
                  <div
                    class="flex items-center justify-center space-x-1 mb-0.5"
                  >
                    <div class="flex items-center space-x-0.5">
                      <i class="uil uil-thumbs-up text-success"></i>
                      <span class="text-xs font-medium text-success">
                        {{ item.positive_feedback_count || 0 }}
                      </span>
                    </div>
                    <span class="text-xs text-muted-foreground">/</span>
                    <div class="flex items-center space-x-0.5">
                      <i class="uil uil-thumbs-down text-destructive"></i>
                      <span class="text-xs font-medium text-destructive">
                        {{ item.negative_feedback_count || 0 }}
                      </span>
                    </div>
                  </div>
                  <span class="text-xs text-muted-foreground">Feedback</span>
                </div>
              </div>
              <div
                v-if="user && user.business && user.business.is_tap_engine"
                class="space-y-1"
              >
                <p class="text-xs text-muted-foreground">Employee URL</p>
                <div
                  class="flex items-center space-x-1 p-2 bg-muted/10 rounded-md"
                >
                  <a
                    :href="item.card_id ? tapUrl + 'c/' + item.card_id : tapUrl + 'r/' + user.business.unique_id + '/' + item.slug"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-primary text-xs font-mono truncate flex-1 hover:underline"
                  >
                    {{
                      item.card_id
                        ? tapUrl + 'c/' + item.card_id
                        : tapUrl +
                          'r/' +
                          user.business.unique_id +
                          '/' +
                          item.slug
                    }}
                  </a>
                  <button
                    type="button"
                    class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:text-accent-foreground rounded-md p-0 hover:bg-primary/10 flex-shrink-0"
                    @click.prevent="
                      copyLink(
                        item.card_id
                          ? tapUrl + 'c/' + item.card_id
                          : tapUrl +
                              'r/' +
                              user.business.unique_id +
                              '/' +
                              item.slug,
                      )
                    "
                  >
                    <i class="uil uil-copy h-2.5 w-2.5"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </template>
        <div v-else class="col-span-full text-center py-8">
          <b-spinner></b-spinner>
        </div>
      </div>
      <div class="flex items-center justify-between mt-4 text-sm text-muted-foreground">
        <span>Total {{ totalEmployees }} employees</span>
        <div class="flex items-center space-x-2">
          <button
            class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-transparent hover:bg-accent hover:text-accent-foreground h-9 rounded-md px-3"
            :disabled="datatable.queries.page === 1"
            @click="datatable.queries.page > 1 && (datatable.queries.page--, onPageChanged())"
          >
            «
          </button>
          <div class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent hover:bg-secondary/80 request-review-btn" style="background-color: rgb(36, 99, 235); color: #ffffff;">
            {{ datatable.queries.page }}
          </div>
          <button
            class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-transparent hover:bg-accent hover:text-accent-foreground h-9 rounded-md px-3"
            :disabled="datatable.queries.page >= Math.ceil(totalEmployees / datatable.queries.per_page)"
            @click="datatable.queries.page < Math.ceil(totalEmployees / datatable.queries.per_page) && (datatable.queries.page++, onPageChanged())"
          >
            »
          </button>
        </div>
      </div>
      </div>
    </div>
    <!-- Employee Form Dialog (Add/Edit) -->
    <div
      v-if="showEmployeeForm"
      role="dialog"
      class="employee-dialog fixed z-50 grid w-full gap-4 border bg-background p-6 shadow-lg duration-200 sm:rounded-lg max-w-2xl"
      :style="{ left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }"
      tabindex="-1"
    >
      <div class="flex flex-col space-y-1.5 text-left sm:text-left">
        <h2 class="tracking-tight text-xl font-semibold text-foreground">{{
          isEditMode ? 'Edit Employee' : 'Add Employee'
        }}</h2>
      </div>
      <ul v-if="Object.keys(errors).length" class="text-danger pl-3 mb-5">
        <template v-for="errorArray in errors">
          <li v-for="(error, key) in errorArray" :key="key">{{ error }}</li>
        </template>
      </ul>
      <ValidationObserver
        v-slot="{ handleSubmit, invalid, touched }"
        ref="employeeForm"
      >
        <form
          class="space-y-6"
          @submit.prevent="
            handleSubmit(isEditMode ? editEmployeeSubmit : addEmployee)
          "
        >
          <div class="space-y-4">
            <div v-if="user && user.business && user.business.is_tap_engine">
              <label
                class="peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-sm font-medium text-foreground"
                for="cardId"
                >Card ID</label
              >
              <div class="flex items-start space-x-4 mt-2">
                <div class="flex-1 space-y-2">
                  <input
                    id="cardId"
                    v-model="currentEmployee.card_id"
                    class="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm w-full"
                    placeholder="Card ID Number"
                    maxlength="7"
                  />
                  <p class="text-xs text-muted-foreground"
                    >Leave blank if you aren't using NFC cards to get reviews</p
                  >
                </div>
                <div class="flex-shrink-0">
                  <div class="relative">
                    <img
                      src="/images/TEBack-small.png"
                      alt="NFC Card"
                      class="w-48 h-auto rounded-lg shadow-lg"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div class="space-y-2">
              <label
                class="peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-sm font-medium text-foreground"
                >Name</label
              >
              <div class="grid grid-cols-2 gap-3">
                <input
                  id="firstName"
                  v-model="currentEmployee.firstName"
                  class="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm w-full"
                  placeholder="First Name"
                  required
                />
                <input
                  id="lastName"
                  v-model="currentEmployee.lastName"
                  class="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm w-full"
                  placeholder="Last Name"
                  required
                />
              </div>
            </div>
            <div
              v-if="user && user.business && user.business.is_tap_engine"
              class="space-y-2"
            >
              <label
                class="peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-sm font-medium text-foreground"
                for="phone"
              >
                Cell Phone
                <span class="text-muted-foreground font-normal"
                  >(optional)</span
                >
              </label>
              <p class="text-xs text-muted-foreground"
                >We will send the leaderboard report to this number</p
              >
              <input
                id="phone"
                v-model="currentEmployee.phone"
                class="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm w-full"
                placeholder="Employee Cell Phone"
              />
              <div class="mt-2">
                <input
                  v-model="currentEmployee.optIn"
                  :checked="currentEmployee.phone !== ''"
                  class="form-check-inline mr-1"
                  type="checkbox"
                />
                <small
                  >I agree to receive text message alerts at this phone number.
                  You can opt out by replying STOP.</small
                >
              </div>
            </div>
            <div
              v-if="user && user.business && user.business.is_tap_engine"
              class="space-y-2"
            >
              <label
                class="peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-sm font-medium text-foreground"
                for="email"
              >
                Email
                <span class="text-muted-foreground font-normal"
                  >(optional)</span
                >
              </label>
              <p class="text-xs text-muted-foreground"
                >We will send the leaderboard report to this email</p
              >
              <input
                id="email"
                v-model="currentEmployee.email"
                type="email"
                class="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm w-full"
                placeholder="Employee Email"
              />
            </div>
          </div>
          <div class="flex items-center justify-end space-x-3 pt-4">
            <button
              class="btn space-x-3 round-xl px-4 btn-secondary"
              type="button"
              @click="closeEmployeeForm"
            >
              Cancel
            </button>
            <button
              class="btn bg-primary space-x-3 round-xl px-4 request-review-btn"
              type="submit"
              :disabled="
                (isEditMode ? loadingUpdate : loadingCreate) ||
                (invalid && touched)
              "
            >
              <b-spinner
                v-if="isEditMode ? loadingUpdate : loadingCreate"
                small
              />
              <span v-else>{{ isEditMode ? 'Update' : 'Create' }}</span>
            </button>
          </div>
        </form>
      </ValidationObserver>
      <a
        type="button"
        class="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
        @click="closeEmployeeForm"
      >
        <i class="uil uil-times h-4 w-4"></i>
        <span class="sr-only">Close</span>
      </a>
    </div>
    <!-- Dialog Overlay -->
    <div
      v-if="showEmployeeForm"
      class="fixed inset-0 z-40 bg-black/50"
      @click="closeEmployeeForm"
    ></div>
    <b-modal v-model="showDeleteModal" title="Delete Employee">
      <p v-if="deletingEmployee"
        >Are you sure you want to delete employee "{{
          deletingEmployee.name
        }}"?</p
      >
      <template v-slot:modal-footer>
        <b-button variant="light" @click="showDeleteModal = false"
          >Cancel</b-button
        >

        <b-button variant="danger" @click="handleRemove">
          <b-spinner v-if="loadingRemove" small />
          <span v-else>Delete</span>
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import Vue from 'vue'
import '../../../design/custom/components/_dashboard.scss'
import { businessLimitForUser } from '@utils/businessLimitsFeature'
import { publicCustomerBaseUrl } from '@utils/reviewGatePublicUrl'

export default {
  components: {},
  data() {
    return {
      errors: [],
      loadingCreate: false,
      loadingUpdate: false,
      loadingEmployees: false,
      loadingRemove: false,
      showEmployeeForm: false,
      isEditMode: false,
      showDeleteModal: false,
      viewMode: 'list',
      currentEmployee: {
        firstName: '',
        lastName: '',
        name: '',
        phone: '',
        email: '',
        slug: null,
        business_id: null,
        optIn: true,
        card_id: null,
      },
      deletingEmployee: null,
      // user: null,
      datatable: {
        items: [],
        totalItems: 1,
        columns: [
          { key: 'id', label: 'ID', sortable: true },
          { key: 'name', label: 'Name', sortable: true },
          { key: 'phone', label: 'Phone Number', sortable: true },
          { key: 'email', label: 'Email', sortable: true },
          { key: 'slug', label: 'Employee URL', sortable: true },
          {
            key: 'positive_feedback_count',
            label: 'Thumbs Up',
            sortable: true,
          },
          {
            key: 'negative_feedback_count',
            label: 'Thumbs Down',
            sortable: true,
          },
          { key: 'actions', label: 'Actions', class: 'actions' },
        ],
        queries: {
          per_page: 10,
          page: 1,
          search: null,
          sort_by: 'id',
          sort_desc: true,
        },
      },
      tapUrl: '', // set in created so Employee URL works on any domain
    }
  },
  created() {
    this.tapUrl = `${publicCustomerBaseUrl()}/`
  },
  computed: {
    employees() {
      return this.$store.getters['employees/all'] || []
    },
    dbColumns() {
      return [
        { key: 'id', label: 'ID', sortable: true },
        { key: 'name', label: 'Name', sortable: true },
        { key: 'phone', label: 'Phone', sortable: true },
        { key: 'email', label: 'Email', sortable: true },
        { key: 'slug', label: 'Employee URL', sortable: true },
        { key: 'rating', label: 'Rating', sortable: true },
        { key: 'mentions_count', label: 'Mentions', sortable: true },
        {
          key: 'positive_feedback_count',
          label: 'Thumbs Up',
          sortable: true,
        },
        {
          key: 'negative_feedback_count',
          label: 'Thumbs Down',
          sortable: true,
        },
        { key: 'actions', label: 'Actions', class: 'actions' },
      ]
    },
    totalEmployees() {
      return this.$store.getters['employees/total']
    },
    leaderBoardReportLink() {
      if (!this.user?.business?.unique_id) return '#'
      return publicCustomerBaseUrl() + '/leaderboard/' + this.user.business.unique_id
    },
    user() {
      return this.$store.getters['auth/user']
    },
  },
  mounted() {
    this.getEmployees()
  },
  methods: {
    openLeaderboard() {
      if (!this.leaderBoardReportLink || this.leaderBoardReportLink === '#') {
        return
      }
      if (typeof window !== 'undefined' && window?.open) {
        window.open(this.leaderBoardReportLink, '_blank')
      }
    },
    showAddEmployee() {
      const employeeLimit = businessLimitForUser(this.user, 'tap_engine_employee_limit')
      if (employeeLimit != null && this.employees.length >= employeeLimit) {
        Vue.$toast.open({
          message: 'Sorry, you have reached your employee limit.',
          type: 'error',
          duration: 5000,
        })
      } else {
        this.isEditMode = false
        this.resetEmployeeForm()
        this.showEmployeeForm = true
      }
    },
    closeEmployeeForm() {
      this.showEmployeeForm = false
      this.isEditMode = false
      this.resetEmployeeForm()
    },
    resetEmployeeForm() {
      this.currentEmployee = {
        firstName: '',
        lastName: '',
        name: '',
        phone: '',
        email: '',
        slug: null,
        business_id: null,
        optIn: true,
        card_id: null,
      }
      this.resetErrors()
    },
    resetErrors() {
      this.errors = []
    },
    copyLink(employeeURL) {
      navigator.clipboard.writeText(employeeURL)
      Vue.$toast.open({
        message: 'Copied',
        type: 'success',
        duration: 5000,
      })
    },
    onPageChanged(page) {
      this.datatable.queries.page = page
      this.getEmployees()
    },
    sortChanged(args) {
      this.datatable.queries.sort_by = args.sortBy
      this.datatable.queries.sort_desc = args.sortDesc
      this.datatable.queries.page = 1
      this.getEmployees()
    },
    handleSort(fieldKey) {
      if (this.datatable.queries.sort_by === fieldKey) {
        // Toggle sort direction
        this.datatable.queries.sort_desc = !this.datatable.queries.sort_desc
      } else {
        // New field, default to descending
        this.datatable.queries.sort_by = fieldKey
        this.datatable.queries.sort_desc = true
      }
      this.datatable.queries.page = 1
      this.getEmployees()
    },
    deleteEmployee(employee) {
      this.deletingEmployee = employee
      this.showDeleteModal = true
    },
    getEmployees() {
      this.loadingEmployees = true

      this.$store
        .dispatch('employees/getAll', {
          queries: this.datatable.queries,
          page: 'index',
        })
        .then((res) => {
          this.loadingEmployees = false
        })
        .catch(() => {
          this.loadingEmployees = false
        })
    },
    editEmployee(employee) {
      this.resetErrors()
      this.isEditMode = true
      this.currentEmployee = { ...employee }
      // Split name into firstName and lastName
      if (this.currentEmployee.name) {
        const nameParts = this.currentEmployee.name.trim().split(/\s+/)
        this.currentEmployee.firstName = nameParts[0] || ''
        this.currentEmployee.lastName = nameParts.slice(1).join(' ') || ''
      } else {
        this.currentEmployee.firstName = ''
        this.currentEmployee.lastName = ''
      }
      this.showEmployeeForm = true
    },
    editEmployeeSubmit() {
      this.loadingUpdate = true
      // Combine firstName and lastName into name
      if (this.currentEmployee.firstName || this.currentEmployee.lastName) {
        this.currentEmployee.name = [
          this.currentEmployee.firstName,
          this.currentEmployee.lastName,
        ]
          .filter((part) => part && part.trim())
          .join(' ')
          .trim()
      }
      // Map to API fields expected by backend
      this.currentEmployee.first_name = this.currentEmployee.firstName || ''
      this.currentEmployee.last_name = this.currentEmployee.lastName || ''
      this.$store
        .dispatch('employees/update', this.currentEmployee)
        .then(() => {
          this.loadingUpdate = false
          this.closeEmployeeForm()
          this.getEmployees()
        })
        .catch((err) => {
          if (err.response.data.errors) {
            this.errors = err.response.data.errors
            this.$refs.employeeForm.setErrors(err.response.data.errors)
          }
          this.loadingUpdate = false
        })
    },
    addEmployee() {
      this.loadingCreate = true
      this.currentEmployee.business_id = this.user.business.id
      this.resetErrors()
      // Combine firstName and lastName into name
      if (this.currentEmployee.firstName || this.currentEmployee.lastName) {
        this.currentEmployee.name = [
          this.currentEmployee.firstName,
          this.currentEmployee.lastName,
        ]
          .filter((part) => part && part.trim())
          .join(' ')
          .trim()
      }
      // Map to API fields expected by backend
      this.currentEmployee.first_name = this.currentEmployee.firstName || ''
      this.currentEmployee.last_name = this.currentEmployee.lastName || ''
      this.$store
        .dispatch('employees/create', this.currentEmployee)
        .then(() => {
          this.loadingCreate = false
          this.getEmployees()
          this.closeEmployeeForm()
        })
        .catch((err) => {
          if (err.response.data.errors) {
            this.errors = err.response.data.errors
            this.$refs.employeeForm.setErrors(err.response.data.errors)
          }
          this.loadingCreate = false
        })
    },
    handleRemove() {
      this.loadingRemove = true
      this.$store
        .dispatch('employees/delete', this.deletingEmployee.id)
        .then(() => {
          this.showDeleteModal = false
          this.loadingRemove = null
          this.getEmployees()
        })
        .catch(() => {
          this.loadingRemove = null
        })
    },
  },
}
</script>
